/**
 * DoctorEarnings.jsx — Personal earnings breakdown for the logged-in doctor.
 * Renders inside DoctorDashboard as a collapsible section.
 * Backend: GET /api/doctor/earnings
 */
import React, { useEffect, useState } from "react";
import { Wallet, TrendingUp, CalendarDays, RefreshCw } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function DoctorEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/doctor/earnings");
      setData(r.data);
    } catch (e) {
      /* silent — doctor may just not have earnings yet */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Card className="p-4 border-slate-200 mt-6 animate-pulse text-sm text-slate-400" data-testid="doctor-earnings-loading">Loading earnings…</Card>;
  if (!data) return null;

  const life = data.lifetime || {};
  const monthly = data.monthly || [];
  const shownMonths = expanded ? monthly : monthly.slice(0, 3);

  return (
    <Card className="p-5 border-slate-200 mt-6" data-testid="doctor-earnings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-600"/>
          <h3 className="font-heading text-lg font-semibold text-slate-900">💰 My Earnings</h3>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">70% doctor share</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="doctor-earnings-refresh">
          <RefreshCw className="w-3.5 h-3.5"/>
        </Button>
      </div>

      {/* Lifetime cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="doctor-earnings-net">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Net Payable</div>
          <div className="mt-1 text-xl font-bold text-emerald-900">{fmt(life.net_doctor_share)}</div>
          <div className="text-[10px] text-emerald-700 mt-0.5">after refunds</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="doctor-earnings-lifetime-share">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Lifetime Doctor Share</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{fmt(life.doctor_share)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">of {fmt(life.revenue)} gross</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="doctor-earnings-paid-cases">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Paid Cases</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{life.paid_cases || 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{life.completed_cases || 0} completed</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="doctor-earnings-refunded">
          <div className="text-[10px] font-bold uppercase tracking-wider text-red-800">Refunded</div>
          <div className="mt-1 text-xl font-bold text-red-900">{life.refunded_cases || 0}</div>
          <div className="text-[10px] text-red-700 mt-0.5">− {fmt(life.refunded_amount)}</div>
        </div>
      </div>

      {/* Monthly table */}
      <div className="mt-4">
        <div className="flex items-center gap-1.5 mb-2">
          <CalendarDays className="w-4 h-4 text-slate-500"/>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Monthly breakdown</div>
        </div>
        {monthly.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No paid cases yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2">Month</th>
                  <th className="py-2 pr-2 text-right">Cases</th>
                  <th className="py-2 pr-2 text-right">Revenue</th>
                  <th className="py-2 pr-2 text-right text-emerald-700">Doctor share</th>
                  <th className="py-2 pr-2 text-right">Platform</th>
                </tr>
              </thead>
              <tbody>
                {shownMonths.map(m => (
                  <tr key={m.month} className="border-b border-slate-100" data-testid={`doctor-earnings-row-${m.month}`}>
                    <td className="py-2 pr-2 font-medium text-slate-900">{m.month}</td>
                    <td className="py-2 pr-2 text-right text-slate-700">{m.paid_cases}</td>
                    <td className="py-2 pr-2 text-right text-slate-700">{fmt(m.revenue)}</td>
                    <td className="py-2 pr-2 text-right text-emerald-700 font-semibold">{fmt(m.doctor_share)}</td>
                    <td className="py-2 pr-2 text-right text-slate-500">{fmt(m.platform_share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {monthly.length > 3 && (
          <button onClick={() => setExpanded(!expanded)} className="mt-2 text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="doctor-earnings-expand">
            <TrendingUp className="w-3.5 h-3.5"/>
            {expanded ? "Show recent 3 months" : `Show all ${monthly.length} months`}
          </button>
        )}
      </div>
    </Card>
  );
}
